import {
  Grid,
  InputAdornment,
  InputLabel,
  makeStyles,
  TextField
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import MotorcycleIcon from "@material-ui/icons/Motorcycle";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import Form from "@saleor/components/Form";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { DropOffLocations_dropOffLocations_edges_node } from "@saleor/dropOff/types/DropOffLocations";
import { checkNumberOnly } from "@saleor/dropOff/utils/validation";
import { maybe } from "@saleor/misc";
import { OrderCourierEnum } from "@saleor/motherOrders/utils/enums";
import { normalizePhoneNumber } from "@saleor/motherOrders/utils/phone";
import { isValidHttpUrl } from "@saleor/motherOrders/utils/url";
import { SmopOrderStatusEnum } from "@saleor/types/globalTypes";
import { mapNodeToChoice } from "@saleor/utils/maps";
import * as React from "react";

import HomeLocationSection from "../components/HomeLocationSection";
import { ThirdPartyInitialFormData } from "../types";

interface ThirdPartyRiderProps {
  initialForm: ThirdPartyInitialFormData;
  setThirdPartyRider: (data: any) => void;
  status: SmopOrderStatusEnum;
  homeLocations?: DropOffLocations_dropOffLocations_edges_node[];
  isOnGoingBooking?: boolean;
  rider?: any;
  pandaGoStatus?: {
    bookedStatus: string[] | null;
    bookingStatus: string[] | null;
  };
}

const useStyles = makeStyles(
  theme => ({
    content: {
      marginBottom: "17px"
    },
    inputLabels: {
      color: "black",
      marginBottom: "5px"
    },
    pandaGoBoxContainer: {
      backgroundColor: "#F4F8FF",
      borderRadius: "8px",
      color: "#002972",
      height: "89px",
      lineHeight: "24px",
      marginBottom: "16px",
      padding: "5px 0px"
    },
    pandaGoDetails: {
      alignItems: "flex-start",
      display: "flex",
      fontSize: "11px",
      height: "72px",
      justifyContent: "center",
      margin: "5px 0px",
      [theme.breakpoints.up("sm")]: {
        fontSize: "18px"
      }
    },
    pandaGoIcon: {
      height: "17px",
      marginTop: "3px",
      width: "17px",
      [theme.breakpoints.up("sm")]: {
        height: "24px",
        marginTop: "0px",
        width: "24px"
      }
    },
    pandaGoInfoIcon: {
      height: "18px",
      verticalAlign: "middle",
      width: "18px"
    },
    pandaGoInfoSpiel: {
      alignItems: "center",
      color: "#747C8C",
      display: "flex",
      fontSize: "17px",
      gap: "3px",
      justifyContent: "center",
      marginTop: "5px"
    },
    riderDetails: {
      alignItems: "left",
      display: "flex",
      flexDirection: "column",
      justifyContent: "left"
    },
    riderDetailsContent: {
      alignItems: "center",
      display: "flex",
      flexWrap: "wrap",
      fontSize: "17px",
      marginBottom: "6px"
    },
    sectionLabel: {
      margin: "0px",
      marginBottom: "6px",
      marginLeft: "5px"
    },
    spacer: {
      marginBottom: "15px",
      marginTop: "15px"
    }
  }),
  { name: "ThirdPartyRider" }
);

const ThirdPartyRider: React.FC<ThirdPartyRiderProps> = props => {
  const {
    initialForm,
    setThirdPartyRider,
    status,
    homeLocations,
    isOnGoingBooking,
    rider,
    pandaGoStatus
  } = props;
  const [isHomeLocationChanged, setIsHomeLocationChanged] = React.useState(
    false
  );
  const [isOutletId, setIsOutletId] = React.useState(false);
  const classes = useStyles(props);
  const riderChoices = maybe(
    () => [
      {
        label: `Grab Express Manual`,
        value: OrderCourierEnum.GRAB
      },
      { label: "Lalamove", value: OrderCourierEnum.LALAMOVE },
      { label: "Others", value: OrderCourierEnum.OTHERS },
      {
        label: `Grab Express Auto`,
        value: OrderCourierEnum.GRAB_EXPRESS
      },
      {
        label: `Pandago Manual`,
        value: OrderCourierEnum.PANDAGO
      },
      {
        label: `Pandago Auto`,
        value: OrderCourierEnum.PANDAGO_AUTO
      }
    ],
    []
  );

  const locationChoices = mapNodeToChoice(homeLocations);

  const pandagoLocationChoices = mapNodeToChoice(
    (homeLocations || []).filter(
      data => data.outletId && data.outletId.trim() !== ""
    )
  );

  const trackingSupportedCouriers: OrderCourierEnum[] = [
    OrderCourierEnum.GRAB,
    OrderCourierEnum.PANDAGO,
    OrderCourierEnum.LALAMOVE,
    OrderCourierEnum.OTHERS
  ];

  return (
    <div>
      <Form initial={initialForm} confirmLeave>
        {({ data: formData, change }) => {
          const selectedHomeLocation = homeLocations?.find(
            loc => loc.id === formData?.dropOffLocationId
          );
          if (formData?.dropOffLocationId && isHomeLocationChanged) {
            const {
              firstName,
              lastName,
              contactNo,
              latitude,
              longitude,
              dropOffAddress,
              remarks
            } = selectedHomeLocation;

            formData.contactPersonFirstName = firstName ?? "";
            formData.contactPersonLastName = lastName ?? "";
            formData.contactPersonContactNo = normalizePhoneNumber(
              contactNo ?? ""
            );
            formData.pickupLatitude = latitude?.toString() ?? "";
            formData.pickupLongitude = longitude?.toString() ?? "";
            formData.pickupLocationName = dropOffAddress ?? "";
            formData.pickupRemarks = remarks ?? "";

            setIsHomeLocationChanged(false);
          }
          setThirdPartyRider(formData);

          const trackingUrl = formData.rider3plTrackingUrl?.trim() ?? "";
          const hasTrackingUrlError =
            trackingUrl !== "" && !isValidHttpUrl(trackingUrl);

          return (
            <>
              <h3
                id="txtAdminFulfillmentAssignRiderDialogCourier"
                className={classes.sectionLabel}
              >
                {" "}
                <InputLabel
                  htmlFor="courier"
                  required
                  style={{ color: "black", fontWeight: 600 }}
                >
                  Courier
                </InputLabel>
              </h3>
              <div
                id="dropdownAdminFulfillmentAssignRiderDialogRiderChoices"
                className={classes.content}
              >
                <SingleSelectField
                  label={"select courier"}
                  onChange={e => {
                    if (
                      e.target.value === OrderCourierEnum.PANDAGO_AUTO ||
                      e.target.value === OrderCourierEnum.PANDAGO
                    ) {
                      setIsOutletId(true);
                    } else {
                      setIsOutletId(false);
                    }
                    change(e);
                  }}
                  choices={riderChoices}
                  name="courier"
                  value={formData.courier}
                  selectProps={{ required: true }}
                  disabled={
                    status === SmopOrderStatusEnum.CANCELED ||
                    status === SmopOrderStatusEnum.COMPLETED ||
                    pandaGoStatus.bookedStatus.includes(
                      rider?.order?.thirdPartyRider?.deliveryCourier?.status
                    )
                  }
                />
              </div>
              <h3
                id="txtAdminFulfillmentAssignRiderDialog3plRiderDetails"
                className={classes.sectionLabel}
              >
                Rider Details
              </h3>
              <div className={classes.riderDetails}>
                <div className={classes.content}>
                  <span
                    id="txtAdminFulfillmentAssignRiderDialog3plNameOfRider"
                    className={classes.riderDetailsContent}
                  >
                    <MotorcycleIcon
                      style={{
                        color: "#9B51E0",
                        fontSize: "33px",
                        marginRight: "10px"
                      }}
                    />
                    Name of Rider
                  </span>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={6}>
                      <TextField
                        id="txtAdminFulfillmentAssignRiderDialog3plRiderFirstName"
                        label={"First Name"}
                        variant="outlined"
                        name="rider3plFirstName"
                        value={formData.rider3plFirstName}
                        onChange={change}
                        fullWidth
                        disabled={
                          status === SmopOrderStatusEnum.CANCELED ||
                          status === SmopOrderStatusEnum.COMPLETED ||
                          formData.courier === OrderCourierEnum.GRAB_EXPRESS ||
                          formData.courier === OrderCourierEnum.PANDAGO_AUTO
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <TextField
                        id="txtAdminFulfillmentAssignRiderDialog3plRiderLastName"
                        label={"Last Name"}
                        variant="outlined"
                        name="rider3plLastName"
                        value={formData.rider3plLastName}
                        onChange={change}
                        fullWidth
                        disabled={
                          status === SmopOrderStatusEnum.CANCELED ||
                          status === SmopOrderStatusEnum.COMPLETED ||
                          formData.courier === OrderCourierEnum.GRAB_EXPRESS ||
                          formData.courier === OrderCourierEnum.PANDAGO_AUTO
                        }
                      />
                    </Grid>
                  </Grid>
                </div>
                <div className={classes.content}>
                  <span
                    id="txtLabelAdminFulfillmentAssignRiderDialog3plMobileNumber"
                    className={classes.riderDetailsContent}
                  >
                    <PhoneAndroidIcon
                      style={{
                        color: "#9B51E0",
                        fontSize: "27px",
                        marginLeft: "4px",
                        marginRight: "13px"
                      }}
                    />
                    Mobile Number
                  </span>
                  <TextField
                    id="txtAdminFulfillmentAssignRiderDialog3plRiderMobileNumber"
                    label={"Mobile Number"}
                    onKeyDown={checkNumberOnly}
                    inputProps={{ placeholder: "ex. 09173134241" }}
                    variant="outlined"
                    fullWidth
                    name="rider3plContactNo"
                    value={formData.rider3plContactNo}
                    onChange={change}
                    disabled={
                      status === SmopOrderStatusEnum.CANCELED ||
                      status === SmopOrderStatusEnum.COMPLETED ||
                      formData.courier === OrderCourierEnum.GRAB_EXPRESS ||
                      formData.courier === OrderCourierEnum.PANDAGO_AUTO
                    }
                  />
                </div>
                {formData.courier === OrderCourierEnum.PANDAGO_AUTO && (
                  <div className={classes.pandaGoBoxContainer}>
                    <Grid
                      className={classes.pandaGoDetails}
                      container
                      spacing={0}
                    >
                      <Grid item xs={1} style={{ margin: "0px" }}>
                        <InfoOutlined className={classes.pandaGoIcon} />
                      </Grid>
                      <Grid item xs={10}>
                        <p style={{ margin: "0px" }}>
                          Once a PandaGo rider has been assigned,{" "}
                          <span style={{ fontWeight: "bold" }}>
                            reassigning the rider or canceling the booking is
                            not permitted.
                          </span>
                        </p>
                      </Grid>
                    </Grid>
                  </div>
                )}
              </div>

              {trackingSupportedCouriers.includes(
                formData.courier as OrderCourierEnum
              ) && (
                <div className={classes.content}>
                  <h3
                    id="txtCourierTrackingLabel"
                    className={classes.sectionLabel}
                  >
                    Courier Tracking
                  </h3>
                  <TextField
                    id="txtCourierTrackingUrl"
                    label={"Paste the tracking link here"}
                    variant="outlined"
                    fullWidth
                    name="rider3plTrackingUrl"
                    value={formData.rider3plTrackingUrl}
                    onChange={change}
                    error={hasTrackingUrlError}
                    helperText={
                      hasTrackingUrlError ? "Enter a valid URL" : undefined
                    }
                    disabled={
                      status === SmopOrderStatusEnum.CANCELED ||
                      status === SmopOrderStatusEnum.COMPLETED
                    }
                  />
                </div>
              )}

              {(formData.courier === OrderCourierEnum.GRAB_EXPRESS ||
                formData.courier === OrderCourierEnum.PANDAGO_AUTO ||
                formData.courier === OrderCourierEnum.PANDAGO) && (
                <>
                  <h3
                    id="txtAdminFulfillmentAssignRiderDialog3plHomeLocation"
                    className={classes.sectionLabel}
                  >
                    {" "}
                    <InputLabel
                      htmlFor="homeLocation"
                      style={{ color: "black", fontWeight: 600 }}
                    >
                      Home Location
                    </InputLabel>
                  </h3>
                  <div
                    id="dropdownAdminFulfillmentAssignRiderDialog3plHomeLocation"
                    className={classes.content}
                  >
                    <SingleSelectField
                      label={"Home Location"}
                      onChange={e => {
                        setIsHomeLocationChanged(true);
                        change(e);
                      }}
                      choices={
                        isOutletId ? pandagoLocationChoices : locationChoices
                      }
                      name="dropOffLocationId"
                      value={formData?.dropOffLocationId}
                      selectProps={{ required: true }}
                      disabled={isOnGoingBooking}
                    />

                    {(formData.courier === OrderCourierEnum.PANDAGO_AUTO ||
                      formData.courier === OrderCourierEnum.PANDAGO) && (
                      <div
                        id="cmpOrderAssignRiderDialogPandagoInfoSpiel"
                        className={classes.pandaGoInfoSpiel}
                      >
                        <InfoOutlined
                          id="iconOrderAssignRiderDialogPandagoInfoSpiel"
                          className={classes.pandaGoInfoIcon}
                        />
                        <span id="txtOrderAssignRiderDialogPandagoInfoSpiel">
                          Only registered home locations will appear in the
                          list.
                        </span>
                      </div>
                    )}
                  </div>
                  <HomeLocationSection
                    formData={formData}
                    onChange={change}
                    rider={rider}
                    disableLatLong={true}
                  />

                  <span
                    id="txtAdminFulfillmentAssignRiderDialog3plHomeLocationContactDetails"
                    className={classes.riderDetailsContent}
                  >
                    <PhoneAndroidIcon
                      style={{
                        color: "#9B51E0",
                        fontSize: "27px",
                        marginLeft: "4px",
                        marginRight: "13px"
                      }}
                    />
                    Home Location Contact Details
                  </span>

                  <div className={classes.content}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={12} md={6}>
                        <TextField
                          id="txtAdminFulfillmentAssignRiderDialog3plFirstName"
                          label={"First Name"}
                          variant="outlined"
                          name="contactPersonFirstName"
                          value={formData?.contactPersonFirstName}
                          onChange={change}
                          fullWidth
                          disabled={isOnGoingBooking}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        <TextField
                          id="txtAdminFulfillmentAssignRiderDialog3plLastName"
                          label={"Last Name"}
                          variant="outlined"
                          name="contactPersonLastName"
                          value={formData?.contactPersonLastName}
                          onChange={change}
                          fullWidth
                          disabled={isOnGoingBooking}
                        />
                      </Grid>
                    </Grid>
                  </div>
                  <div className={classes.content}>
                    <TextField
                      id="txtAdminFulfillmentAssignRiderDialogContactNum"
                      label={"Contact Number"}
                      onKeyDown={checkNumberOnly}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            style={{ paddingTop: "15px", zIndex: 2 }}
                          >
                            +63
                          </InputAdornment>
                        )
                      }}
                      variant="outlined"
                      fullWidth
                      name="contactPersonContactNo"
                      value={formData?.contactPersonContactNo}
                      onChange={event => {
                        if (event.target.value.length > 10) {
                          return;
                        }
                        change(event);
                      }}
                      disabled={isOnGoingBooking}
                    />
                  </div>
                  <div className={classes.content}>
                    <TextField
                      id="txtAdminFulfillmentAssignRiderDialog3plDropoffAddress"
                      label={"Address Line Based on Pin"}
                      variant="outlined"
                      fullWidth
                      name="pickupLocationName"
                      value={formData?.pickupLocationName}
                      onChange={change}
                      disabled
                    />
                  </div>
                  <Grid container className={classes.spacer}>
                    <Grid item xs={12} sm={12} md={12}>
                      <TextField
                        id="txtAdminFulfillmentAssignRiderDialog3plRemarks"
                        label="Remarks"
                        variant="outlined"
                        name="pickupRemarks"
                        value={formData?.pickupRemarks}
                        onChange={change}
                        fullWidth
                        disabled
                        multiline
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          );
        }}
      </Form>
    </div>
  );
};

export default ThirdPartyRider;
